import * as React from "react";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/hooks/useI18n";
import {
  searchUnsplashPhotos,
  trackUnsplashDownload,
  unsplashPhotoToMediaItem,
  UnsplashPhoto,
} from "@/services/unsplash.service";
import { MediaItem, withUnsplashUtm } from "@/types/media.types";

interface UnsplashPickerProps {
  /**
   * Riceve il MediaItem già completo di attribuzione. Il tracking del download
   * richiesto dalle Unsplash API Guidelines è già stato notificato qui dentro.
   */
  onSelect: (item: MediaItem) => void;
  onClose: () => void;
  /** Copre sia il `disabled` del form sia il limite max immagini raggiunto. */
  disabled?: boolean;
  perPage?: number;
  className?: string;
}

/**
 * Pannello di ricerca e selezione foto Unsplash.
 *
 * Unico punto in cui vivono i requisiti delle Unsplash API Guidelines lato
 * selezione: attribuzione visibile sui risultati, link al profilo del fotografo
 * con parametri di referral, e notifica del download alla scelta. Tenerli in un
 * solo componente evita che una modifica futura ne aggiorni solo una copia.
 */
export const UnsplashPicker = ({
  onSelect,
  onClose,
  disabled = false,
  perPage = 12,
  className = "",
}: UnsplashPickerProps) => {
  const { t } = useI18n();
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState<UnsplashPhoto[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleSearch = async () => {
    if (!query.trim() || disabled) return;

    setLoading(true);
    setError(null);
    try {
      const { results: photos } = await searchUnsplashPhotos({ query, per_page: perPage });
      setResults(photos);
    } catch (err) {
      console.error("Error searching Unsplash photos:", err);
      setError(t("modals.unsplashSearchError"));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSelect = (photo: UnsplashPhoto) => {
    if (disabled) return;
    // La selezione è l'azione "equivalente a un download" per Unsplash: va
    // notificata qui e non al rendering dei risultati.
    void trackUnsplashDownload(photo.downloadLocation);
    onSelect(unsplashPhotoToMediaItem(photo));
  };

  return (
    <div className={`space-y-3 p-4 border border-gray-200 rounded-lg bg-gray-50 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-gray-600" />
          <span className="font-medium text-sm text-gray-700">{t("modals.searchUnsplash")}</span>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={disabled}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex gap-2">
        <Input
          type="text"
          placeholder={t("modals.unsplashSearchPlaceholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || loading}
          className="flex-1"
        />
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={handleSearch}
          disabled={disabled || loading || !query.trim()}
        >
          {loading ? t("common.loading") : t("common.search")}
        </Button>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {!loading && !error && results.length === 0 && query && (
        <p className="text-sm text-gray-500">{t("modals.unsplashNoResults")}</p>
      )}

      {results.length > 0 && (
        <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto">
          {results.map((photo) => (
            <div
              key={photo.id}
              role="button"
              tabIndex={disabled ? -1 : 0}
              onClick={() => handleSelect(photo)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSelect(photo);
              }}
              aria-disabled={disabled}
              className={`relative group rounded-md overflow-hidden border border-gray-200 ${
                disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-blue-400"
              }`}
            >
              <img
                src={photo.thumbUrl}
                alt={photo.description ?? ""}
                className="w-full h-20 object-cover"
              />
              {/* Attribuzione richiesta dalle Unsplash API Guidelines anche sui risultati. */}
              <a
                href={withUnsplashUtm(photo.author.link)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-1 py-0.5 truncate hover:underline"
                title={t("modals.unsplashPhotoBy", { name: photo.author.name })}
              >
                {photo.author.name}
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UnsplashPicker;
